// File: src/pages/CurrentActivities.jsx
// Description: Current Activities page - fetches activities from backend
// with bilingual support (EN/MM), sort by latest/earliest, and detail modal with gallery

import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import '../styles/pages/current-activities.css';

// ─── API Base URL ─────────────────────────────────────────────────────────────
const BASE_URL = 'https://api.mahavihara-international.org';

// ─── Bilingual UI Labels ──────────────────────────────────────────────────────
const LABELS = {
  en: {
    pageTitle: 'Current Activities',
    pageSubtitle: 'Explore what we are doing right now',
    latest: 'Latest First',
    earliest: 'Earliest First',
    readMore: 'Read More',
    close: 'Close',
    noData: 'No activities found.',
    loading: 'Loading activities...',
    error: 'Failed to load activities. Please try again.',
    retry: 'Retry',
    gallery: 'Gallery',
    postedOn: 'Posted on',
  },
  mm: {
    pageTitle: 'လက်ရှိလှုပ်ရှားမှုများ',
    pageSubtitle: 'ကျွန်ုပ်တို့ ယခုလုပ်ဆောင်နေသည်များကို ကြည့်ရှုပါ',
    latest: 'နောက်ဆုံးအရင်',
    earliest: 'အစောဆုံးအရင်',
    readMore: 'ဆက်ဖတ်ရန်',
    close: 'ပိတ်ရန်',
    noData: 'လှုပ်ရှားမှုများ မတွေ့ပါ။',
    loading: 'လှုပ်ရှားမှုများ ဖွင့်နေသည်...',
    error: 'လှုပ်ရှားမှုများ ဖွင့်မရပါ။ ထပ်မံကြိုးစားပါ။',
    retry: 'ထပ်ကြိုးစားရန်',
    gallery: 'ဓာတ်ပုံများ',
    postedOn: 'တင်သည့်ရက်',
  },
};

// ─── Sort options ─────────────────────────────────────────────────────────────
const SORT = { LATEST: 'latest', EARLIEST: 'earliest' };

// ─── Helper: format date ──────────────────────────────────────────────────────
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

// ─── Helper: resolve relative path to full URL ────────────────────────────────
const resolveUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `${BASE_URL}/${path}`;
};

// ─── Sub-component: Activity Card ─────────────────────────────────────────────
const ActivityCard = ({ activity, lang, labels, onOpen }) => {
  const title = lang === 'mm' ? activity.title_mm : activity.title_en;
  const body  = lang === 'mm' ? activity.body_mm  : activity.body_en;
  const galleryCount = activity.more_photos?.length || 0;

  return (
    <article
      className="activity-card scroll-fade hover-lift"
      onClick={() => onOpen(activity)}
    >
      {/* Header image */}
      {activity.header_photo && (
        <div className="activity-card__image">
          <img
            src={resolveUrl(activity.header_photo)}
            alt={title}
            loading="lazy"
          />
          {/* Gallery count badge */}
          {galleryCount > 0 && (
            <span className="activity-card__gallery-badge">
              📷 +{galleryCount}
            </span>
          )}
        </div>
      )}

      <div className="activity-card__body">
        {/* Date */}
        <span className="activity-card__date">
          📅 {formatDate(activity.created_at)}
        </span>

        {/* Title */}
        <h3 className={`activity-card__title ${lang === 'mm' ? 'font-myanmar' : ''}`}>
          {title}
        </h3>

        {/* Body preview — strip HTML, limit 120 chars */}
        <p className={`activity-card__preview ${lang === 'mm' ? 'font-myanmar' : ''}`}>
          {body?.replace(/<[^>]+>/g, '').slice(0, 120)}
          {body?.length > 120 ? '...' : ''}
        </p>

        <button className="btn-primary activity-card__btn">
          {labels.readMore}
        </button>
      </div>
    </article>
  );
};

// ─── Sub-component: Activity Detail Modal ─────────────────────────────────────
const ActivityModal = ({ activity, lang, labels, onClose }) => {
  if (!activity) return null;

  const title = lang === 'mm' ? activity.title_mm : activity.title_en;
  const body  = lang === 'mm' ? activity.body_mm  : activity.body_en;
  const photos = activity.more_photos || [];

  // Close on backdrop click
  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="activity-modal__backdrop animate-fade-in-down"
      onClick={handleBackdrop}
    >
      <div className="activity-modal__box animate-zoom-in">

        {/* Close button */}
        <button
          className="activity-modal__close"
          onClick={onClose}
          aria-label="Close"
        >
          ✕
        </button>

        {/* Header photo */}
        {activity.header_photo && (
          <div className="activity-modal__header-img">
            <img src={resolveUrl(activity.header_photo)} alt={title} />
          </div>
        )}

        <div className="activity-modal__content">
          {/* Date */}
          <span className="activity-card__date">
            {labels.postedOn}: {formatDate(activity.created_at)}
          </span>

          {/* Title */}
          <h2 className={`activity-modal__title ${lang === 'mm' ? 'font-myanmar' : ''}`}>
            {title}
          </h2>

          {/* Body — rendered as HTML */}
          <div
            className={`activity-modal__body ${lang === 'mm' ? 'font-myanmar' : ''}`}
            dangerouslySetInnerHTML={{ __html: body }}
          />

          {/* Gallery grid */}
          {photos.length > 0 && (
            <div className="activity-modal__gallery-section">
              <h4 className="activity-modal__section-title">
                📷 {labels.gallery}
              </h4>
              <div className="activity-modal__gallery">
                {photos.map((p, i) => (
                  <a
                    key={i}
                    href={resolveUrl(p.photo_path)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src={resolveUrl(p.photo_path)}
                      alt={`gallery-${i + 1}`}
                      loading="lazy"
                    />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main Page Component ──────────────────────────────────────────────────────
const CurrentActivities = () => {
  // currentLanguage: 'en' | 'my'
  const { currentLanguage } = useLanguage();
  const lang   = currentLanguage === 'my' ? 'mm' : 'en';
  const labels = LABELS[lang];

  const [activities, setActivities]         = useState([]);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState(null);
  const [sort, setSort]                     = useState(SORT.LATEST);
  const [selectedActivity, setSelected]     = useState(null);

  // ── Fetch activities ──
  const fetchActivities = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BASE_URL}/current-activities`);
      if (!res.ok) throw new Error('Network error');
      const data = await res.json();
      // Response: { success, message, data: [...] }
      setActivities(data.data || data);
    } catch {
      setError(labels.error);
    } finally {
      setLoading(false);
    }
  }, [labels.error]);

  useEffect(() => { fetchActivities(); }, [fetchActivities]);

  // ── Scroll-fade observer ──
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e =>
        e.target.classList.toggle('visible', e.isIntersecting)
      ),
      { threshold: 0.1 }
    );
    document.querySelectorAll('.scroll-fade').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [activities]);

  // ── Sort by created_at ──
  const sorted = [...activities].sort((a, b) => {
    const da = new Date(a.created_at);
    const db = new Date(b.created_at);
    return sort === SORT.LATEST ? db - da : da - db;
  });

  return (
    <div className="activities-page">

      {/* ── Page Banner ── */}
      <div className="page-banner">
        <div className="container">
          <div className="banner-content">
            <h1 className={`banner-title ${lang === 'mm' ? 'font-myanmar' : ''}`}>
              {labels.pageTitle}
            </h1>
            <p className={`banner-subtitle ${lang === 'mm' ? 'font-myanmar' : ''}`}>
              {labels.pageSubtitle}
            </p>
          </div>
        </div>
      </div>

      {/* ── Sort Controls ── */}
      <div className="activities-controls container">
        <div className="activities-sort">
          <button
            className={`activities-sort-btn ${sort === SORT.LATEST ? 'active' : ''}`}
            onClick={() => setSort(SORT.LATEST)}
          >
            🕐 <span className={lang === 'mm' ? 'font-myanmar' : ''}>{labels.latest}</span>
          </button>
          <button
            className={`activities-sort-btn ${sort === SORT.EARLIEST ? 'active' : ''}`}
            onClick={() => setSort(SORT.EARLIEST)}
          >
            🕰 <span className={lang === 'mm' ? 'font-myanmar' : ''}>{labels.earliest}</span>
          </button>
        </div>
      </div>

      {/* ── Activities Grid ── */}
      <section className="activities-grid-section section-padding">
        <div className="container">

          {/* Loading */}
          {loading && (
            <div className="activities-state text-center">
              <div className="spinner-ring" />
              <p className={lang === 'mm' ? 'font-myanmar' : ''}>{labels.loading}</p>
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <div className="activities-state activities-state--error text-center">
              <p>{error}</p>
              <button className="btn-primary" onClick={fetchActivities}>
                {labels.retry}
              </button>
            </div>
          )}

          {/* Empty */}
          {!loading && !error && sorted.length === 0 && (
            <div className="activities-state text-center">
              <p className={lang === 'mm' ? 'font-myanmar' : ''}>{labels.noData}</p>
            </div>
          )}

          {/* Grid */}
          {!loading && !error && sorted.length > 0 && (
            <div className="activities-grid">
              {sorted.map(activity => (
                <ActivityCard
                  key={activity.id}
                  activity={activity}
                  lang={lang}
                  labels={labels}
                  onOpen={setSelected}
                />
              ))}
            </div>
          )}

        </div>
      </section>

      {/* ── Detail Modal ── */}
      {selectedActivity && (
        <ActivityModal
          activity={selectedActivity}
          lang={lang}
          labels={labels}
          onClose={() => setSelected(null)}
        />
      )}

    </div>
  );
};

export default CurrentActivities;